import { LinkProvider, type LinkProps } from "@crew/ui/components/link";
import { TooltipProvider } from "@crew/ui/components/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  Link as RouterLink,
  useParams,
} from "react-router-dom";
import { RequireMe, RequireSignedOut } from "./app/guards";
import { listenForInstall, registerServiceWorker } from "./app/install";
import { useMe } from "./app/me";
import { queryClient } from "./app/query";
import { HOME } from "./app/home";
import { AppShell } from "./app/shell";
import { LoginPage } from "./features/auth/login";
import { SignupPage } from "./features/auth/signup";
import { VerifyPage } from "./features/auth/verify";
import { CheckoutPage } from "./features/billing/checkout";
import { ActivityRoute } from "./features/activity/activity-page";
import { AreaPage } from "./features/areas/area-page";
import { BriefPage } from "./features/areas/brief-page";
import { AgentRoute } from "./features/agent/agent-page";
import { ChangelogPage } from "./features/changelog/changelog-page";
import { ChatsPage } from "./features/chats/chats-page";
import { ComputerPage } from "./features/computer/computer-page";
import { DocPage, NewDocPage } from "./features/docs/doc-page";
import { DocsPage } from "./features/docs/docs-page";
import { MemoryPage } from "./features/memory/memory-page";
import { JoinPage } from "./features/people/join-page";
import { PeopleRoute as PeoplePageRoute } from "./features/people/people-page";
import { AppPage } from "./features/apps/app-page";
import { AppsPage } from "./features/apps/apps-page";
import { RoutineDetailPage } from "./features/routines/routine-detail";
import { RoutinesPage } from "./features/routines/routines-page";
import { SettingsPage } from "./features/settings/settings-page";
import { SetupPage } from "./features/setup/setup-page";
import { TaskDetailPage } from "./features/tasks/task-detail";
import { TasksPage } from "./features/tasks/tasks-page";
import { TodayPage } from "./features/today/today-page";
import "./index.css";
import { initSentry } from "./sentry";

initSentry();
listenForInstall();
registerServiceWorker();

/** The SPA lives under /hq (ADR-0013); an old link like /today lands inside it. */
const BASE = "/hq";
if (location.pathname !== BASE && !location.pathname.startsWith(`${BASE}/`)) {
  location.replace(`${BASE}${location.pathname}${location.search}${location.hash}`);
}

/** Today reads the same `/api/me` the shell's guard already resolved. */
function TodayRoute() {
  const { data: me } = useMe();
  return me ? <TodayPage me={me} /> : null;
}

function SettingsRoute() {
  const { data: me } = useMe();
  return me ? <SettingsPage me={me} /> : null;
}

/** Browse apps (slice 3b spec §5.6): under Settings → Integrations. */
function BrowseAppsRoute() {
  const { data: me } = useMe();
  return me ? <SettingsPage me={me} sub="apps" /> : null;
}

/** What asks first (slice 3 §3.1): the owner's, under Settings → Integrations. */
function AskFirstRoute() {
  const { data: me } = useMe();
  return me ? <SettingsPage me={me} sub="ask-first" /> : null;
}

function MemoryRoute() {
  const { data: me } = useMe();
  return me ? <MemoryPage me={me} /> : null;
}

function ActivityPageRoute() {
  const { data: me } = useMe();
  return me ? <ActivityRoute me={me} /> : null;
}

/** Slice 6i: the apps this person can see. */
function AppsRoute() {
  const { data: me } = useMe();
  return me ? <AppsPage me={me} /> : null;
}

/** Slice 6i: an agent-built app. */
function AppRoute() {
  const { appId = "" } = useParams();
  return <AppPage key={appId} appId={appId} />;
}

function PeopleRoute() {
  const { data: me } = useMe();
  return me ? <PeoplePageRoute me={me} /> : null;
}

function CrewRoute() {
  const { data: me } = useMe();
  return me ? <AgentRoute me={me} /> : null;
}

function TasksRoute() {
  const { data: me } = useMe();
  return me ? <TasksPage me={me} /> : null;
}

function AreaRoute() {
  const { data: me } = useMe();
  const { slug = "" } = useParams();
  return me ? <AreaPage key={slug} slug={slug} me={me} /> : null;
}

function BriefRoute() {
  const { data: me } = useMe();
  const { slug = "" } = useParams();
  return me ? <BriefPage key={slug} slug={slug} me={me} /> : null;
}

function TaskRoute() {
  const { data: me } = useMe();
  const { taskId = "" } = useParams();
  return me ? <TaskDetailPage key={taskId} taskId={taskId} me={me} /> : null;
}

function RoutinesRoute() {
  const { data: me } = useMe();
  return me ? <RoutinesPage me={me} /> : null;
}

function RoutineRoute() {
  const { data: me } = useMe();
  const { routineId = "" } = useParams();
  return me ? <RoutineDetailPage key={routineId} routineId={routineId} me={me} /> : null;
}

/** The Computer page (slice 6c §5): reached from Settings → Tools. */
function ComputerRoute() {
  const { data: me } = useMe();
  return me ? <ComputerPage me={me} /> : null;
}

function ChangelogRoute() {
  const { data: me } = useMe();
  return me ? <ChangelogPage userId={me.user.id} /> : null;
}

function DocsRoute() {
  const { data: me } = useMe();
  return me ? <DocsPage me={me} /> : null;
}

function DocRoute() {
  const { data: me } = useMe();
  const { docId = "" } = useParams();
  if (!me) return null;
  return docId === "new" ? <NewDocPage me={me} /> : <DocPage key={docId} docId={docId} me={me} />;
}

function RouterLinkAdapter({ href, ...props }: LinkProps) {
  return <RouterLink to={href} {...props} />;
}

/** What every page sits in: links through the router, and tooltips. */
function Root() {
  return (
    <LinkProvider component={RouterLinkAdapter}>
      <TooltipProvider>
        <Outlet />
      </TooltipProvider>
    </LinkProvider>
  );
}

/** A data router, so a page can ask before it's left (`useBlocker`: slice 1j §3.8). */
const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<Root />}>
      <Route
        path="/login"
        element={
          <RequireSignedOut>
            <LoginPage />
          </RequireSignedOut>
        }
      />
      <Route
        path="/signup"
        element={
          <RequireSignedOut>
            <SignupPage />
          </RequireSignedOut>
        }
      />
      {/* An invite's link (slice 6a §6.3): no session needed, and one already here is replaced. */}
      <Route path="/join/:token" element={<JoinPage />} />
      <Route
        path="/verify"
        element={<RequireMe need="verify">{(me) => <VerifyPage me={me} />}</RequireMe>}
      />
      <Route
        path="/checkout"
        element={<RequireMe need="pending">{(me) => <CheckoutPage me={me} />}</RequireMe>}
      />
      <Route
        path="/setup"
        element={
          <RequireMe need="setup" linger>
            {(me) => <SetupPage me={me} />}
          </RequireMe>
        }
      />
      <Route element={<RequireMe need="active">{(me) => <AppShell me={me} />}</RequireMe>}>
        <Route path="/today" element={<TodayRoute />} />
        <Route path="/chats" element={<ChatsPage />} />
        <Route path="/chats/:threadId" element={<ChatsPage />} />
        <Route path="/settings" element={<SettingsRoute />} />
        <Route path="/settings/:tab" element={<SettingsRoute />} />
        <Route path="/settings/integrations/apps" element={<BrowseAppsRoute />} />
        <Route path="/settings/integrations/ask-first" element={<AskFirstRoute />} />
        <Route path="/memory" element={<MemoryRoute />} />
        <Route path="/people" element={<PeopleRoute />} />
        <Route path="/crew/:slug" element={<CrewRoute />} />
        <Route path="/activity" element={<ActivityPageRoute />} />
        <Route path="/activity/:id" element={<ActivityPageRoute />} />
        <Route path="/areas/:slug" element={<AreaRoute />} />
        <Route path="/areas/:slug/brief" element={<BriefRoute />} />
        <Route path="/tasks" element={<TasksRoute />} />
        <Route path="/tasks/:taskId" element={<TaskRoute />} />
        <Route path="/routines" element={<RoutinesRoute />} />
        <Route path="/routines/:routineId" element={<RoutineRoute />} />
        <Route path="/computer" element={<ComputerRoute />} />
        <Route path="/docs" element={<DocsRoute />} />
        <Route path="/apps" element={<AppsRoute />} />
        <Route path="/apps/:appId" element={<AppRoute />} />
        <Route path="/changelog" element={<ChangelogRoute />} />
        <Route path="/docs/:docId" element={<DocRoute />} />
      </Route>
      {/* `/` and any unknown path: home. */}
      <Route path="*" element={<Navigate to={HOME} replace />} />
    </Route>,
  ),
  { basename: BASE },
);

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
